import { ChevronLeft } from 'lucide-react';
import { SkloKruh } from '../../components/ui/Sklo';

/**
 * Stavební prvky rozvozového cockpitu — podle normy UI (standard-rozvrzeni.md).
 *
 * - Plochy jsou neutrální grafit z `app.css` (`--surface-*`), nikdy modrý
 *   nebo fialový podklad (feedback „Žádná modrá v pozadí"). Barevně se kreslí
 *   jen akce a stavy.
 * - Barva sekce je oranžová (`moduly.js` → `rozvoz-cockpit`); stavy mají
 *   pevné barvy: hotovo zeleně, problém červeně, pozor jantarově.
 * - Cockpit se drží v ruce za jízdy: hlavní akce 60 px, ostatní ≥ 44 px,
 *   hlavní text 15–24 px.
 * - Horní ovládání je sklo (`SkloKruh`), stejně jako jinde na mobilu.
 *
 * Návrh obrazovek: artefakt „Rozvoz cockpit – návrh obrazovek" (2026-10-11).
 */
export const AKCENT = '#FF8A00';
export const OK = '#10B981';
export const CHYBA = '#EF4444';
export const POZOR = '#F59E0B';

/** Jak moc barvu ztlumit pro podklad — jen u tlačítek a štítků, nikdy u plochy. */
export const tint = (hex, a) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

export const kc = (n) => `${Math.round(n || 0).toLocaleString('cs-CZ')} Kč`;

export function hm(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

/** Návrat — skleněný kruh u horní hrany (norma: vlevo návrat, vpravo akce). */
export function Zpet({ onClick, isDark, popis = 'Zpět do aplikace' }) {
    return <SkloKruh Icon={ChevronLeft} ikona={22} popis={popis} onClick={onClick} isDark={isDark} />;
}

/** Plocha karty / spodního listu. */
export function plocha({ zaobleni = 18, uroven = 'raised' } = {}) {
    return {
        background: uroven === 'elevated' ? 'var(--surface-elevated)' : 'var(--surface-raised)',
        border: '1px solid var(--border-default)',
        borderRadius: zaobleni,
        color: 'var(--text-primary)',
    };
}

/** Spodní list nad mapou — zaoblený jen nahoře, sahá ke spodní hraně. */
export function spodniList() {
    return {
        background: 'var(--surface-raised)',
        borderTop: '1px solid var(--border-default)',
        borderRadius: '24px 24px 0 0',
        color: 'var(--text-primary)',
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    };
}

/**
 * Tlačítko cockpitu. `plne` = hlavní akce obrazovky (nejvýš jedna), jinak
 * ztlumené v barvě akce, nebo neutrální, když barva chybí.
 */
export function Tlacitko({ children, barva, plne = false, vyska = 60, Icon, onClick, href, disabled, style, svisle = false, ...rest }) {
    const vzhled = plne && barva
        ? { background: barva, color: '#0A0A0C', border: `1px solid ${barva}` }
        : barva
            ? { background: tint(barva, 0.14), color: barva, border: `1.5px solid ${tint(barva, 0.5)}` }
            : { background: 'var(--surface-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' };
    const props = {
        style: {
            minHeight: vyska, borderRadius: 16, padding: '0 14px',
            display: 'flex', flexDirection: svisle ? 'column' : 'row', alignItems: 'center', justifyContent: 'center',
            gap: svisle ? 4 : 8, fontSize: plne ? 17 : 15, fontWeight: plne ? 900 : 800,
            textDecoration: 'none', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
            WebkitTapHighlightColor: 'transparent', textAlign: 'center',
            ...vzhled, ...style,
        },
        ...rest,
    };
    const obsah = <>{Icon && <Icon size={svisle ? 20 : 19} strokeWidth={2.4} />}{children}</>;
    return href && !disabled
        ? <a href={href} {...props}>{obsah}</a>
        : <button type="button" onClick={onClick} disabled={disabled} {...props}>{obsah}</button>;
}

/** Malý štítek stavu (zaplaceno online, kartou 248 Kč…). */
export function Stitek({ barva, children, velky = false }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: velky ? 13 : 12, fontWeight: 800, padding: velky ? '6px 10px' : '3px 8px', borderRadius: 8,
            background: barva ? tint(barva, 0.16) : 'var(--surface-elevated)',
            color: barva ?? 'var(--text-secondary)',
            border: barva ? `1px solid ${tint(barva, 0.4)}` : '1px solid var(--border-default)',
        }}>{children}</span>
    );
}

/** Nadpis sekce — kapitálky, tiše. */
export function Nadpis({ children, barva, style }) {
    return (
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: barva ?? 'var(--text-secondary)', ...style }}>
            {children}
        </div>
    );
}

/**
 * Jak chce zákazník u dveří platit — podle volby v objednávce
 * (`typ_platby`: „Kartou", „Hotově"; „Online platba" je už zaplacená).
 */
export function ocekavanaPlatba(z) {
    return /kart|card/i.test(String(z?.typ_platby ?? '')) ? 'kartou' : 'hotove';
}

/** Platba zastávky jako štítek — co rozvozce u dveří řeší. */
export function stitekPlatby(z) {
    const castka = kc(z?.castka_kc);
    switch (z?.stav_platby) {
        case 'online_zaplaceno': return { barva: OK, text: 'zaplaceno online' };
        case 'zaplaceno_hotove': return { barva: OK, text: `zaplaceno hotově ${castka}` };
        case 'zaplaceno_kartou': return { barva: OK, text: `zaplaceno kartou ${castka}` };
        default: return { barva: AKCENT, text: `${ocekavanaPlatba(z) === 'kartou' ? 'kartou' : 'hotově'} ${castka}` };
    }
}

/** Odkaz do navigace Mapy.cz (aplikace v telefonu ho otevře sama). */
export function odkazNavigace(z) {
    if (z?.lat == null || z?.lng == null) return null;
    // Mobilní aplikace: `geo:` otevře navigaci, kterou má rozvozce v telefonu
    // (Mapy.com, Google Mapy, Waze…) — Android nabídne výběr nebo rovnou
    // výchozí. Web (i iPhone) zůstává u odkazu na Mapy.com.
    if (window.__FL_APLIKACE) return `geo:${z.lat},${z.lng}?q=${z.lat},${z.lng}`;
    return `https://mapy.com/fnc/v1/route?end=${z.lng},${z.lat}&routeType=car_fast`;
}

/**
 * Celá trasa po pořadí: objednávky a mezi nimi benzinky / obchody
 * (2026-10-29). Zastávka leží za objednávkou `za_objednavkou_id`
 * (null = hned po výjezdu); za objednávkou, která v trase není, na konci.
 *
 * @return {{typ: 'o'|'m', klic: number|string, z?: object, m?: object}[]}
 */
export function sestavTrasu(zastavky = [], mista = []) {
    const ids = new Set(zastavky.map(z => z.objednavka_id));
    const zaMisto = (m) => ({ typ: 'm', klic: m.klic ?? `m${m.id}`, m });
    const out = mista.filter(m => m.za_objednavkou_id == null).map(zaMisto);
    zastavky.forEach(z => {
        out.push({ typ: 'o', klic: z.objednavka_id, z });
        mista.filter(m => m.za_objednavkou_id === z.objednavka_id).forEach(m => out.push(zaMisto(m)));
    });
    mista.filter(m => m.za_objednavkou_id != null && !ids.has(m.za_objednavkou_id)).forEach(m => out.push(zaMisto(m)));
    return out;
}

/** Benzinka / obchod — popisek činnosti na místě. */
export const cinnostMista = (druh) => (druh === 'benzinka' ? 'tankování' : 'nákup');
